import { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { TableCell } from "./ui/table";
import { Button } from "./ui/button";
import { Check, X } from "lucide-react";

interface EditableTDProps {
  value: string;
  /** Optional formatted value shown when not editing (e.g. comma-separated numbers). */
  displayValue?: string;
  onChange: (newValue: string) => void;
  isFirstRow?: boolean;
  /** Shown when value is empty (e.g. "---") */
  placeholder?: string;
  /** Skip guessing from the current value. Length must stay text even when it looks numeric. */
  inputType?: "text" | "number" | "date";
}

function EditableTD({
  value,
  displayValue,
  onChange,
  isFirstRow = false,
  placeholder = "---",
  inputType: inputTypeProp,
}: EditableTDProps) {
  const [editing, setEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value);
  const [inputType, setInputType] = useState<string>(inputTypeProp ?? "text");

  // Function to determine input type based on value
  const determineInputType = (val: string): string => {
    if (inputTypeProp) return inputTypeProp;
    // Check for number (including decimals)
    if (/^-?\d*\.?\d+$/.test(val)) {
      return "number";
    }
    // Check for date (YYYY-MM-DD format or similar)
    if (/^\d{4}-\d{2}-\d{2}$/.test(val) || !isNaN(Date.parse(val))) {
      return "date";
    }
    // Default to text
    return "text";
  };

  useEffect(() => {
    setInputValue(value);
    setInputType(determineInputType(value));
    if (isFirstRow && value === undefined) {
      setEditing(true);
      setInputType("text"); // Default to text for empty first row
    }
  }, [value, isFirstRow, inputTypeProp]);

  const handleSave = () => {
    setEditing(false);
    onChange(inputValue);
  };

  const handleCancel = () => {
    setEditing(false);
    setInputValue(value);
    setInputType(determineInputType(value));
  };

  return (
    <TableCell>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            type={inputType}
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setInputValue(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              e.key === "Enter" && handleSave()
            }
            autoFocus
            className="flex-1 min-w-[150px]"
            {...(inputType === "number" && {
              step: "any",
              min: undefined,
              max: undefined,
            })}
            {...(inputType === "date" && {
              min: undefined,
              max: undefined,
            })}
          />
          <Button variant="outline" size="sm" onClick={handleSave}>
            <Check className="text-green-600 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleCancel}>
            <X className="text-red-600 w-4" />
          </Button>
        </div>
      ) : (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
            cursor: "pointer",
          }}
          onClick={() => setEditing(true)}
        >
          {value !== undefined && value !== null && String(value).trim() !== "" && String(value).trim().toLowerCase() !== "undefined"
            ? (displayValue ?? value)
            : placeholder}
        </span>
      )}
    </TableCell>
  );
}

export default EditableTD;
